"use client";
import React, { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { SearchCard } from '@/components/molecules/SearchCard';
import { SidebarFilter, FilterOption } from '@/components/molecules/Filter';
import PageSizeSelector from '@/components/atoms/PageSizeSelector';
import { Pagination } from '@/components/molecules/Pagination';
import DocumentCard from '@/components/molecules/DocumentCard';

interface DocumentosPageClientProps {
    documentosData: {
        documentos: any[];
        documentosDestaque?: any[];
        pagination: {
            total: number;
            page: number;
            pageSize: number;
            pageCount: number;
        };
    };
    filtros: FilterOption[];
}

export default function DocumentosPageClient({ documentosData, filtros }: DocumentosPageClientProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    console.log('[DOCUMENTOS CLIENT] Filtros recebidos:', JSON.stringify(filtros, null, 2));

    // Get current search parameters
    // const currentSearch = searchParams.get('search') || '';
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const currentPageSize = parseInt(searchParams.get('pageSize') || '10', 10);

    // Build current filters object
    const currentFilters: { [key: string]: string } = {};
    filtros.forEach(filter => {
        const value = searchParams.get(filter.value);
        if (value) {
            currentFilters[filter.value] = value;
        }
    });

    
    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.set('page', page.toString());
        router.push(`/documentos?${params.toString()}`);
    };

    const handlePageSizeChange = (pageSize: number) => {
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.set('pageSize', pageSize.toString());
        params.set('page', '1'); // Reset to first page
        router.push(`/documentos?${params.toString()}`);
    };

    

    const toggleMobileFilter = () => {
        setIsMobileFilterOpen(!isMobileFilterOpen);
    };

    return (
        <div className="min-h-screen bg-white">
            <div className="container mx-auto px-4 py-6 sm:py-8">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                {/* Desktop Sidebar */}
                <div className={`hidden lg:block w-full transition-all pt-6 duration-300 ${isSidebarOpen ? 'lg:w-1/4' : 'lg:w-auto'}`}>
                    <SidebarFilter
                        data={filtros}
                        onToggleDesktopFilter={(visible) => setIsSidebarOpen(visible)}
                    />
                </div>

                {/* Main Content */}
                <div className={`w-full transition-all duration-300 ${isSidebarOpen ? 'lg:w-3/4' : 'lg:w-full'}`}>
                    {/* Search and Controls */}
                    <div className="mb-8 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6 lg:gap-12 px-2 sm:px-4 py-2 sm:py-6">
                            <div className="flex-1 max-w-full sm:max-w-[500px] lg:max-w-[710px] lg:ml-auto">
                                <SearchCard />
                            </div>
                            <div className="flex-shrink-0 self-end sm:self-center">
                                <PageSizeSelector
                                    className="w-auto ml-auto"
                                    value={currentPageSize}
                                    options={[5, 10, 20, 50]}
                                    onChange={handlePageSizeChange}
                                />
                            </div>
                            
                        </div>
                    </div>                    

                    {/* Documents Grid */}
                    {(documentosData.documentosDestaque && documentosData.documentosDestaque.length > 0) || documentosData.documentos.length > 0 ? (
                        <>
                            {/* Documentos em Destaque - Sempre visíveis, sem filtros */}
                            {documentosData.documentosDestaque && documentosData.documentosDestaque.length > 0 && (
                                <div className="mb-12">
                                    <h2 className="text-2xl font-semibold font-rubik text-[#2A2A2A] mb-6">
                                        Documentos em Destaque
                                    </h2>
                                    <div className="grid grid-cols-1 gap-6">
                                        {documentosData.documentosDestaque.map((documento, index) => (
                                            <DocumentCard
                                                key={documento.id || `destaque-${index}`}
                                                document={documento}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Outros Documentos - Afetados por filtros e busca */}
                            {documentosData.documentos.length > 0 ? (
                                <div className="mb-8">
                                    {documentosData.documentosDestaque && documentosData.documentosDestaque.length > 0 && (
                                        <h2 className="text-2xl font-semibold font-rubik text-[#2A2A2A] mb-6">
                                            Outros Documentos
                                        </h2>
                                    )}
                                    <div className="grid grid-cols-1 gap-6">
                                        {documentosData.documentos.map((documento, index) => (
                                            <DocumentCard
                                                key={documento.id || `normal-${index}`}
                                                document={documento}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                documentosData.documentosDestaque && documentosData.documentosDestaque.length > 0 && (
                                    <div className="text-center py-12">
                                        <div className="text-gray-500 text-lg mb-4">Nenhum outro documento encontrado</div>
                                        <p className="text-gray-400">Tente ajustar os filtros ou termo de pesquisa</p>
                                    </div>
                                )
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <div className="text-gray-500 text-lg mb-4">Nenhum documento encontrado</div>
                            <p className="text-gray-400">Tente ajustar os filtros ou termo de pesquisa</p>
                        </div>
                    )}

                    {/* Pagination */}
                    {documentosData.pagination.pageCount > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={documentosData.pagination.pageCount}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
                </div>
            </div>

            {/* Mobile Filter Modal */}
            {isMobileFilterOpen && (
                <div className="lg:hidden fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={toggleMobileFilter}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold">Filtros</h3>
                                    <button
                                        onClick={toggleMobileFilter}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <SidebarFilter
                                    data={filtros}
                                    isMobile
                                    onCloseMobile={toggleMobileFilter}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
